// src/api/reportApi.js
import apiClient from "./axios";

const reportApi = {

getInventoryReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    object_id,
    resource,
    action_type,
    search = "",
    report_type,
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    export: exportFormat,
    // Advanced filters
    status,
    reorder_suggested,
    available_quantity__gte,
    available_quantity__lte,
    buffer_threshold__gte,
    buffer_threshold__lte,
    category,
    sub_category,
    // Any other custom filters
    ...restParams
  } = params;

  const isExport = exportFormat === "csv";

  // Clean params: remove undefined values but keep empty strings if needed
  const cleanParams = (obj) =>
    Object.fromEntries(
      Object.entries(obj).filter(([_, v]) => v !== undefined && v !== null)
    );

  const response = await apiClient.get("/inventories/reports/", {
    params: cleanParams({
      page,
      page_size: isExport ? undefined : page_size,
      object_id: object_id || undefined,
      resource: resource || undefined,
      action_type: action_type || undefined,
      search: search.trim() || undefined,
      report_type: report_type || undefined,
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      export: isExport ? "csv" : undefined,
      // Advanced filters
      status: status || undefined,
      reorder_suggested: reorder_suggested || undefined,
      available_quantity__gte: available_quantity__gte || undefined,
      available_quantity__lte: available_quantity__lte || undefined,
      buffer_threshold__gte: buffer_threshold__gte || undefined,
      buffer_threshold__lte: buffer_threshold__lte || undefined,
      category: category || undefined,
      sub_category: sub_category || undefined,
      // Pass through any other params
      ...restParams, 
    }),
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }

  return {
    data: response.data,           // { summary, results }
    pagination: response.data.pagination || response.pagination || {
      count: response.data.results?.length || 0,
      total_pages: 1,
      current_page: 1,
      page_size: page_size,
    },
  };
},

// ===================================================================
// STOCK MOVEMENT REPORT API (with export)
// ===================================================================
getStockMovementReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    export: exportFormat,
    report_type,
    // Add advanced filters
    period,
    purchased__gte,
    purchased__lte,
    consumed__gte,
    consumed__lte,
    net_change__gte,
    net_change__lte,
  } = params;

  const isExport = exportFormat === "csv";

  const response = await apiClient.get("/inventories/reports/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type: report_type || "stock_movement",
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      export: isExport ? "csv" : undefined,
      // Add advanced filters
      ...(period && { period }),
      ...(purchased__gte && { purchased__gte }),
      ...(purchased__lte && { purchased__lte }),
      ...(consumed__gte && { consumed__gte }),
      ...(consumed__lte && { consumed__lte }),
      ...(net_change__gte && { net_change__gte }),
      ...(net_change__lte && { net_change__lte }),
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},

getLowStockReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    export: exportFormat,
    report_type,
    // Add advanced filters
    status,
    category,
    sub_category,
    current_quantity__gte,
    current_quantity__lte,
    shortfall__gte,
    shortfall__lte,
    buffer_threshold__gte,
    buffer_threshold__lte,
  } = params;

  const isExport = exportFormat === "csv";

  const response = await apiClient.get("/inventories/reports/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type: report_type || "low_stock",
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      export: isExport ? "csv" : undefined,
      // Add advanced filters
      ...(status && { status }),
      ...(category && { category }),
      ...(sub_category && { sub_category }),
      ...(current_quantity__gte && { current_quantity__gte }),
      ...(current_quantity__lte && { current_quantity__lte }),
      ...(shortfall__gte && { shortfall__gte }),
      ...(shortfall__lte && { shortfall__lte }),
      ...(buffer_threshold__gte && { buffer_threshold__gte }),
      ...(buffer_threshold__lte && { buffer_threshold__lte }),
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},
getNearExpiryReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    export: exportFormat,
    report_type,
    // Add advanced filters
    status,
    category,
    sub_category,
    days__gte,
    days__lte,
    near_days,
  } = params;

  const isExport = exportFormat === "csv";

  const response = await apiClient.get("/inventories/reports/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type: report_type || "expired_near_expiry",
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      export: isExport ? "csv" : undefined,
      // Add advanced filters
      ...(status && { status }),
      ...(category && { category }),
      ...(sub_category && { sub_category }),
      ...(days__gte && { days__gte }),
      ...(days__lte && { days__lte }),
      ...(near_days && { near_days }),
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},

getConsumptionTrendReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    export: exportFormat,
    report_type,
    // Add advanced filters
    sub_category,
    total_consumed__gte,
    recent_consumed__gte,
    average_daily__gte,
    spike_detected,
  } = params;

  const isExport = exportFormat === "csv";

  const response = await apiClient.get("/inventories/reports/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type: report_type || "consumption_trend",
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      export: isExport ? "csv" : undefined,
      // Add advanced filters
      ...(sub_category && { sub_category }),
      ...(total_consumed__gte && { total_consumed__gte }),
      ...(recent_consumed__gte && { recent_consumed__gte }),
      ...(average_daily__gte && { average_daily__gte }),
      ...(spike_detected && { spike_detected }),
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},

  // NEW: Canteen Expiry Report using /api/inventories/canteen_expiry_report/
  
getPurchaseHistoryReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    export: exportFormat,
    report_type,
    // Add advanced filters
    vendor,
    category,
    sub_category,
    quantity__gte,
    quantity__lte,
    amount__gte,
    amount__lte,
  } = params;

  const isExport = exportFormat === "csv";

  const response = await apiClient.get("/inventories/reports/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type: report_type || "purchase_history",
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      export: isExport ? "csv" : undefined,
      // Add advanced filters
      ...(vendor && { vendor }),
      ...(category && { category }),
      ...(sub_category && { sub_category }),
      ...(quantity__gte && { quantity__gte }),
      ...(quantity__lte && { quantity__lte }),
      ...(amount__gte && { amount__gte }),
      ...(amount__lte && { amount__lte }),
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},
  
  getCanteenExpiryReport: async (params = {}) => {
    const {
      page = 1,
      page_size = 10,
      object_id,
      resource,
      action_type,
      search = "",
      date_range,
      start_date,
      end_date,
      from_datetime,
      to_datetime,
      category,
      sub_category,
      vendor,
      expiry_status, // 'expired', 'about_to_expire', 'all'
      min_expiry_days,
      max_expiry_days,
    } = params;

    const response = await apiClient.get(
      "/inventories/canteen_expiry_report/",
      {
        params: {
          page,
          page_size,
          object_id: object_id || undefined,
          resource: resource || undefined,
          action_type: action_type || undefined,
          search: search.trim() || undefined,
          date_range: date_range || undefined,
          start_date: start_date || undefined,
          end_date: end_date || undefined,
          from_datetime: from_datetime || undefined,
          to_datetime: to_datetime || undefined,
          category: category || undefined,
          sub_category: sub_category || undefined,
          vendor: vendor || undefined,
          expiry_status: expiry_status || undefined,
          min_expiry_days: min_expiry_days || undefined,
          max_expiry_days: max_expiry_days || undefined,
        },
      }
    );

    return response.data;
  },

  getConsumptionTrend: async (params = {}) => {
    const response = await apiClient.get("/reports/consumption-trend/", {
      params,
    });
    return response.data; // Expect { items: [], chartData: [], summary: {} }
  },

  getDepartmentConsumption: async (params = {}) => {
    const response = await apiClient.get("/reports/department-consumption/", {
      params,
    });
    return response.data; // Expect { departments: [], summary: {} }
  },

// src/api/reportApi.js
getPriceVariationReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    export: exportFormat,
    report_type,
    // Add advanced filters
    vendor,
    unit_price__gte,
    unit_price__lte,
    variation__gte,
    variation__lte,
    variation_pct__gte,
    variation_pct__lte,
    trend,
    category,
    sub_category,
  } = params;

  const isExport = exportFormat === "csv";

  const response = await apiClient.get("/inventories/reports/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type: report_type || "price_variation",
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      export: isExport ? "csv" : undefined,
      // Add advanced filters
      ...(vendor && { vendor }),
      ...(unit_price__gte && { unit_price__gte }),
      ...(unit_price__lte && { unit_price__lte }),
      ...(variation__gte && { variation__gte }),
      ...(variation__lte && { variation__lte }),
      ...(variation_pct__gte && { variation_pct__gte }),
      ...(variation_pct__lte && { variation_pct__lte }),
      ...(trend && { trend }),
      ...(category && { category }),
      ...(sub_category && { sub_category }),
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},
  getDepartmentConsumptionReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    export: exportFormat,
    report_type,
    // Add advanced filters
    department,
    sub_category,
    total_consumed__gte,
    total_consumed__lte,
  } = params;

  const isExport = exportFormat === "csv";

  const response = await apiClient.get("/inventories/reports/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type: report_type || "department_consumption",
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      export: isExport ? "csv" : undefined,
      // Add advanced filters
        ...(department && { department }),
      ...(sub_category && { sub_category }),
      ...(total_consumed__gte && { total_consumed__gte }),
      ...(total_consumed__lte && { total_consumed__lte }),
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},

// Update the getAssetReport function to include new filters
getAssetReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    object_id,
    resource,
    action_type,
    search = "",
    type,
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    export: exportFormat,
    // New filters for asset_master
    category,
    sub_category,
    vendor,
    is_assigned,
    min_quantity,
    max_quantity,
    min_value,
    max_value,
    // New filters for allocation
    employee,
    // New filters for warranty
    days,
  } = params;

  const isExport = exportFormat === "csv";

  const response = await apiClient.get("/asset-assigns/reports/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      object_id: object_id || undefined,
      resource: resource || undefined,
      action_type: action_type || undefined,
      search: search.trim() || undefined,
      type: type || undefined,
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      // Add new filters
      ...(category && { category }),
      ...(sub_category && { sub_category }),
      ...(vendor && { vendor }),
      ...(is_assigned && { is_assigned }),
      ...(min_quantity && { min_quantity }),
      ...(max_quantity && { max_quantity }),
      ...(min_value && { min_value }),
      ...(max_value && { max_value }),
      ...(employee && { employee }),
      ...(days && { days }),
      export: isExport ? "csv" : undefined,
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }

  // For export requests, return the entire response object so the thunk can access the blob
  return response;
},

// Add this function to reportApi.js
getNewAssetLifecycleReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    report_type = "it_assignment_inventory_life_cycle", // Fixed type
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    category,
    sub_category,
    export: exportFormat,
  } = params;

  const isExport = exportFormat === "csv";

  const response = await apiClient.get("/inventories/reports/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type,
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      category: category || undefined,
      sub_category: sub_category || undefined,
      export: isExport ? "csv" : undefined,
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response;
},

  getAssetMaster: async () => {
    const response = await apiClient.get("/reports/asset-master/");
    return response.data; // Expect array of assets
  },

  getWarrantyExpiry: async () => {
    const response = await apiClient.get("/reports/warranty-expiry/");
    return response.data; // Expect array of assets with expiry info
  },

  getAssetAllocation: async () => {
    const response = await apiClient.get("/reports/asset-allocation/");
    return response.data; // Expect array of allocated assets
  },

  getIdleAssets: async () => {
    const response = await apiClient.get("/reports/Idle-assets/");
    return response.data; // Expect array of idle assets
  },

  getAssetLifecycleCost: async () => {
    const response = await apiClient.get("/reports/asset-lifecycle-cost/");
    return response.data; // Expect array of assets with cost details
  },

//////////// all ticjet /////////////////

getOpenClosedReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    export: exportFormat,
    report_type,
    // Add advanced filters
    status,
    ticket_code,
    assigned_to,
    created_by_department,
    created_by_name,
    issue_type,
    break_not_availed,
    priority,
    period_start,
    period_end,
  } = params;

  const isExport = exportFormat === "csv";

  const response = await apiClient.get("/tickets/report/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type: report_type || "open_closed",
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      export: isExport ? "csv" : undefined,
      // Add advanced filters
      ...(status && { status }),
      ...(ticket_code && { ticket_code }),
      ...(assigned_to && { assigned_to }),
      ...(created_by_department && { created_by_department }),
      ...(created_by_name && { created_by_name }),
      ...(issue_type && { issue_type }),
      ...(break_not_availed && { break_not_availed }),
      ...(priority && { priority }),
      ...(period_start && { period_start }),
      ...(period_end && { period_end }),
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},

getTATReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    export: exportFormat,
    report_type,
    // Add advanced filters
    status,
    ticket_code,
    assigned_to,
    created_by_department,
    created_by_name,
    issue_type,
    priority,
    tat_min,
    tat_max,
    period_start,
    period_end,
  } = params;

  const isExport = exportFormat === "csv";

  const response = await apiClient.get("/tickets/report/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type: "tat_compliance",
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      export: isExport ? "csv" : undefined,
      // Add advanced filters
      ...(status && { status }),
      ...(ticket_code && { ticket_code }),
      ...(assigned_to && { assigned_to }),
      ...(created_by_department && { created_by_department }),
      ...(created_by_name && { created_by_name }),
      ...(issue_type && { issue_type }),
      ...(priority && { priority }),
      ...(tat_min && { tat_min }),
      ...(tat_max && { tat_max }),
      ...(period_start && { period_start }),
      ...(period_end && { period_end }),
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},
// Update the getEscalationReport function to include new filters
getEscalationReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    status, // Add status parameter
    priority, // Add priority parameter
    assigned_to, // Add assigned_to parameter
    created_by_department, // Add created_by_department parameter
    assigned_to_department, // Add assigned_to_department parameter
    sub_category, // Add sub_category parameter
    is_overdue, // Add is_overdue parameter
    export: exportCsv,
  } = params;

  const isExport = exportCsv === "csv";

  const response = await apiClient.get("/tickets/report/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type: "escalation",
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      status: status || undefined, // Add status to params
      priority: priority || undefined, // Add priority to params
      assigned_to: assigned_to || undefined, // Add assigned_to to params
      created_by_department: created_by_department || undefined, // Add created_by_department to params
      assigned_to_department: assigned_to_department || undefined, // Add assigned_to_department to params
      sub_category: sub_category || undefined, // Add sub_category to params
      is_overdue: is_overdue || undefined, // Add is_overdue to params
      export: isExport ? "csv" : undefined,
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},


getDepartmentVolumeReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    department_id, // Add department_id parameter
    ticket_count_min, // Add ticket_count_min parameter
    ticket_count_max, // Add ticket_count_max parameter
    export: exportCsv,
  } = params;

  const isExport = exportCsv === "csv";

  const response = await apiClient.get("/tickets/report/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type: "department_volume",
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      department_id: department_id || undefined, // Add department_id to params
      ticket_count_min: ticket_count_min || undefined, // Add ticket_count_min to params
      ticket_count_max: ticket_count_max || undefined, // Add ticket_count_max to params
      export: isExport ? "csv" : undefined,
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},

getRecurringIssuesReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    category, // Add category parameter
    sub_category, // Add sub_category parameter
    issue_count_min, // Add issue_count_min parameter
    issue_count_max, // Add issue_count_max parameter
    export: exportCsv,
  } = params;

  const isExport = exportCsv === "csv";

  const response = await apiClient.get("/tickets/report/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type: "recurring_issues",
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      category: category || undefined, // Add category to params
      sub_category: sub_category || undefined, // Add sub_category to params
      issue_count_min: issue_count_min || undefined, // Add issue_count_min to params
      issue_count_max: issue_count_max || undefined, // Add issue_count_max to params
      export: isExport ? "csv" : undefined,
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},

getHandlerProductivityReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    assigned_to, // Add assigned_to parameter
    min_assigned, // Add min_assigned parameter
    max_assigned, // Add max_assigned parameter
    min_resolved, // Add min_resolved parameter
    max_resolved, // Add max_resolved parameter
    export: exportCsv,
  } = params;

  const isExport = exportCsv === "csv";

  const response = await apiClient.get("/tickets/report/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type: "handler_productivity",
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      assigned_to: assigned_to || undefined, // Add assigned_to to params
      min_assigned: min_assigned || undefined, // Add min_assigned to params
      max_assigned: max_assigned || undefined, // Add max_assigned to params
      min_resolved: min_resolved || undefined, // Add min_resolved to params
      max_resolved: max_resolved || undefined, // Add max_resolved to params
      export: isExport ? "csv" : undefined,
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},

  getBreakReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    export: exportFormat,
    report_type,
    // Add advanced filters
    employee,
    title,
    break_not_availed,
    titles,
    shift_type,
    not_on_desk_reason,
    period_start,
    period_end,
  } = params;

  const isExport = exportFormat === "csv";

  const response = await apiClient.get("/break-time-logs/reports/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type: "summary",
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      export: isExport ? "csv" : undefined,
      // Add advanced filters
      ...(employee && { employee }),
      ...(title && { title }),
      ...(titles && { titles }),
      ...(shift_type && { shift_type }),
      ...(break_not_availed && { break_not_availed }),
      ...(not_on_desk_reason && { not_on_desk_reason }),
      ...(period_start && { period_start }),
      ...(period_end && { period_end }),
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},

getReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    export: exportFormat,
    report_type,
    // Add advanced filters
    employee,
    title,
    titles,
    shift_type,
    not_on_desk_reason,
    period_start,
    period_end,
  } = params;

  const isExport = exportFormat === "csv";

  const response = await apiClient.get("/break-time-logs/reports/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type: "total_duration",
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      export: isExport ? "csv" : undefined,
      // Add advanced filters
      ...(employee && { employee }),
      ...(title && { title }),
      ...(titles && { titles }),
      ...(shift_type && { shift_type }),
      ...(not_on_desk_reason && { not_on_desk_reason }),
      ...(period_start && { period_start }),
      ...(period_end && { period_end }),
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},

  // src/api/reportApi.js
getBreakIrregularActivityReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    export: exportFormat,
    report_type,
    // Add advanced filters
    employee,
    title,
    issue_type,
    break_not_availed,
    shift_type,
    not_on_desk_reason,
    period_start,
    period_end,
  } = params;

  const isExport = exportFormat === "csv";

  const response = await apiClient.get("/break-time-logs/reports/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type: "irregular_activity",
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      export: isExport ? "csv" : undefined,
      // Add advanced filters
      ...(employee && { employee }),
      ...(title && { title }),
      ...(issue_type && { issue_type }),
      ...(break_not_availed && { break_not_availed }),
      ...(shift_type && { shift_type }),
      ...(not_on_desk_reason && { not_on_desk_reason }),
      ...(period_start && { period_start }),
      ...(period_end && { period_end }),
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},
getBreakTimeDeviationReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    export: exportFormat,
    report_type,
    // Add advanced filters
    employee,
    title,
    deviation_type,
    deviation_min,
    deviation_max,
    other,
  } = params;

  const isExport = exportFormat === "csv";

  const response = await apiClient.get("/break-time-logs/reports/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type: "time_deviation",
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      export: isExport ? "csv" : undefined,
      // Add advanced filters
      ...(employee && { employee }),
      ...(title && { title }),
      ...(deviation_type && { deviation_type }),
      ...(deviation_min && { deviation_min }),
      ...(deviation_max && { deviation_max }),
      ...(other && { other }),
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},


  ////////////////////

  getBreakSummary: async (params = {}) => {
    const {
      page = 1,
      page_size = 10,
      object_id,
      resource,
      action_type,
      search = "",
      date_range,
      start_date,
      end_date,
      from_datetime,
      to_datetime,
    } = params;

    const response = await apiClient.get("/break-time-logs/", {
      params: {
        page,
        page_size,
        object_id: object_id || undefined,
        resource: resource || undefined,
        action_type: action_type || undefined,
        search: search.trim() || undefined,
        date_range: date_range || undefined,
        start_date: start_date || undefined,
        end_date: end_date || undefined,
        from_datetime: from_datetime || undefined,
        to_datetime: to_datetime || undefined,
      },
    });

    return response.data;
  },

  getBreakDuration: async (params = {}) => {
    const response = await apiClient.get("/reports/break-duration/", {
      params,
    });
    return response.data; // Expect { employees: [], summary: {} }
  },

  getIrregularBreaks: async (params = {}) => {
    const response = await apiClient.get("/reports/irregular-breaks/", {
      params,
    });
    return response.data; // Expect { irregularities: [], summary: {} }
  },

  getTimeDeviation: async (params = {}) => {
    const response = await apiClient.get("/reports/time-deviation/", {
      params,
    });
    return response.data; // Expect { deviations: [], summary: {} }
  },

  getTicketReport: async (params = {}) => {
    const {
      page = 1,
      page_size = 10,
      // Ticket-specific filters (adjust based on your backend contract)
      ticket_id,
      status,
      priority,
      assigned_to,
      created_by,
      department,
      category,
      subcategory,

      // General filters
      search = "",
      date_range, // e.g., "last7days", "this_month", etc. (if backend supports)
      start_date, // YYYY-MM-DD
      end_date, // YYYY-MM-DD
      from_datetime, // ISO datetime
      to_datetime, // ISO datetime
    } = params;

    const response = await apiClient.get("/tickets/report/", {
      params: {
        page,
        page_size,

        // Ticket filters
        ticket_id: ticket_id || undefined,
        status: status || undefined,
        priority: priority || undefined,
        assigned_to: assigned_to || undefined,
        created_by: created_by || undefined,
        department: department || undefined,
        category: category || undefined,
        subcategory: subcategory || undefined,

        // Search & Date filters
        search: search.trim() || undefined,
        date_range: date_range || undefined,
        start_date: start_date || undefined,
        end_date: end_date || undefined,
        from_datetime: from_datetime || undefined,
        to_datetime: to_datetime || undefined,
      },
    });

    return response.data;
  },

  getTatCompliance: async (params = {}) => {
    const response = await apiClient.get("/reports/tat-compliance/", {
      params,
    });
    return response.data; // Expect { compliance: [], departments: [], summary: {} }
  },

  // NEW: Escalation History using /api/escalation-history/
  getEscalationHistory: async (params = {}) => {
    const {
      page = 1,
      page_size = 10,
      object_id,
      resource,
      action_type,
      search = "",
      date_range,
      start_date,
      end_date,
      from_datetime,
      to_datetime,
      content_type__model, // 'ticket'
      department,
      escalation_level,
      escalated_to,
      reason,
    } = params;

    const response = await apiClient.get("/escalation-history/", {
      params: {
        page,
        page_size,
        object_id: object_id || undefined,
        resource: resource || undefined,
        action_type: action_type || undefined,
        search: search.trim() || undefined,
        date_range: date_range || undefined,
        start_date: start_date || undefined,
        end_date: end_date || undefined,
        from_datetime: from_datetime || undefined,
        to_datetime: to_datetime || undefined,
        content_type__model: content_type__model || undefined,
        department: department || undefined,
        escalation_level: escalation_level || undefined,
        escalated_to: escalated_to || undefined,
        reason: reason || undefined,
      },
    });

    return response.data;
  },

  // NEW: Get Ticket Details for Escalations
  getTicketDetails: async (ticketId) => {
    const response = await apiClient.get(`/tickets/${ticketId}/`);
    return response.data;
  },

  getDepartmentTickets: async (params = {}) => {
    const response = await apiClient.get("/reports/department-tickets/", {
      params,
    });
    return response.data; // Expect { departments: [], summary: {} }
  },

  getRecurringIssues: async (params = {}) => {
    const response = await apiClient.get("/reports/recurring-issues/", {
      params,
    });
    return response.data; // Expect { issues: [], summary: {} }
  },

  getHandlerProductivity: async (params = {}) => {
    const response = await apiClient.get("/reports/handler-productivity/", {
      params,
    });
    return response.data; // Expect { handlers: [], summary: {} }
  },



  /// cnateen all repoet ///////////////////////////////////

// getCanteenOrderReport: async (params = {}) => {
//     const {
//       page = 1,
//       page_size = 10,
//       search = "",
//       date_range,
//       start_date,
//       end_date,
//       from_datetime,
//       to_datetime,
//       export: exportCsv,
//     } = params;

//     const isExport = exportCsv === "csv";

//     const response = await apiClient.get("/orders/reports/", {
//       params: {
//         page,
//         page_size: isExport ? undefined : page_size,
//         search: search.trim() || undefined,
//         report_type: "order_summary",           // Fixed for this report
//         date_range: date_range || undefined,
//         start_date: start_date || undefined,
//         end_date: end_date || undefined,
//         from_datetime: from_datetime || undefined,
//         to_datetime: to_datetime || undefined,
//         export: isExport ? "csv" : undefined,
//       },
//       responseType: isExport ? "blob" : "json",
//     });

//     if (!isExport) {
//       return response.data;
//     }
//     return response; // blob for export
//   },


// src/api/reportApi.js

// Update the getCanteenOrderReport function to include new filters
getCanteenOrderReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    report_type = "order_summary",
    // group_by = "daily",
    status, // Add status parameter
    // employee, // Add employee parameter
    min_revenue, // Add min_revenue parameter
    max_revenue, // Add max_revenue parameter
    export: exportCsv,
  } = params;

  const isExport = exportCsv === "csv";

  const response = await apiClient.get("/orders/reports/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type,
      // group_by,
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      status: status || undefined, // Add status to params
      // employee: employee || undefined, // Add employee to params
      min_revenue: min_revenue || undefined, // Add min_revenue to params
      max_revenue: max_revenue || undefined, // Add max_revenue to params
      export: isExport ? "csv" : undefined,
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},

  // getPopularItemsReport: async (params = {}) => {
  //   const {
  //     page = 1,
  //     page_size = 10,
  //     search = "",
  //     date_range,
  //     start_date,
  //     end_date,
  //     from_datetime,
  //     to_datetime,
  //     export: exportCsv,
  //   } = params;

  //   const isExport = exportCsv === "csv";

  //   const response = await apiClient.get("/orders/reports/", {
  //     params: {
  //       page,
  //       page_size: isExport ? undefined : page_size,
  //       search: search.trim() || undefined,
  //       report_type: "popular_items",           // Fixed for this report only
  //       date_range: date_range || undefined,
  //       start_date: start_date || undefined,
  //       end_date: end_date || undefined,
  //       from_datetime: from_datetime || undefined,
  //       to_datetime: to_datetime || undefined,
  //       export: isExport ? "csv" : undefined,
  //     },
  //     responseType: isExport ? "blob" : "json",
  //   });

  //   if (!isExport) {
  //     return response.data;
  //   }
  //   return response; // blob for export
  // },

  // src/api/reportApi.js

// Update the getPopularItemsReport function to include new filters
getPopularItemsReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    report_type = "popular_items",
    menu, // Add menu parameter
    include_zero_sales, // Add include_zero_sales parameter
    only_in_stock, // Add only_in_stock parameter
    highlight_high_demand, // Add highlight_high_demand parameter
    demand_status, // Add demand_status parameter
    min_quantity, // Add min_quantity parameter
    max_quantity, // Add max_quantity parameter
    min_revenue, // Add min_revenue parameter
    ordering, // Add ordering parameter
    export: exportCsv,
  } = params;

  const isExport = exportCsv === "csv";

  const response = await apiClient.get("/orders/reports/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type,
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      menu: menu || undefined, // Add menu to params
      include_zero_sales: include_zero_sales !== undefined ? include_zero_sales : undefined, // Add include_zero_sales to params
      only_in_stock: only_in_stock !== undefined ? only_in_stock : undefined, // Add only_in_stock to params
      highlight_high_demand: highlight_high_demand !== undefined ? highlight_high_demand : undefined, // Add highlight_high_demand to params
      demand_status: demand_status || undefined, // Add demand_status to params
      min_quantity: min_quantity || undefined, // Add min_quantity to params
      max_quantity: max_quantity || undefined, // Add max_quantity to params
      min_revenue: min_revenue || undefined, // Add min_revenue to params
      ordering: ordering || undefined, // Add ordering to params
      export: isExport ? "csv" : undefined,
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},


  // getCanteenExpenseReport: async (params = {}) => {
  //   const {
  //     page = 1,
  //     page_size = 10,
  //     search = "",
  //     date_range,
  //     start_date,
  //     end_date,
  //     from_datetime,
  //     to_datetime,
  //     export: exportCsv,
  //   } = params;

  //   const isExport = exportCsv === "csv";

  //   const response = await apiClient.get("/orders/reports/", {
  //     params: {
  //       page,
  //       page_size: isExport ? undefined : page_size,
  //       search: search.trim() || undefined,
  //       report_type: "expense_collection",     // Fixed for this report only
  //       date_range: date_range || undefined,
  //       start_date: start_date || undefined,
  //       end_date: end_date || undefined,
  //       from_datetime: from_datetime || undefined,
  //       to_datetime: to_datetime || undefined,
  //       export: isExport ? "csv" : undefined,
  //     },
  //     responseType: isExport ? "blob" : "json",
  //   });

  //   if (!isExport) {
  //     return response.data;
  //   }
  //   return response; // blob for export
  // },

  // src/api/reportApi.js

// Update the getCanteenExpenseReport function to include new filters
getCanteenExpenseReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    report_type = "expense_collection",
    // group_by = "day",
    min_profit, // Add min_profit parameter
    max_profit, // Add max_profit parameter
    export: exportCsv,
  } = params;

  const isExport = exportCsv === "csv";

  const response = await apiClient.get("/orders/reports/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type,
      // group_by,
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      min_profit: min_profit || undefined, // Add min_profit to params
      max_profit: max_profit || undefined, // Add max_profit to params
      export: isExport ? "csv" : undefined,
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},


  // getIngredientStockReport: async (params = {}) => {
  //   const {
  //     page = 1,
  //     page_size = 10,
  //     search = "",
  //     date_range,
  //     start_date,
  //     end_date,
  //     from_datetime,
  //     to_datetime,
  //     export: exportCsv,
  //   } = params;

  //   const isExport = exportCsv === "csv";

  //   const response = await apiClient.get("/orders/reports/", {
  //     params: {
  //       page,
  //       page_size: isExport ? undefined : page_size,
  //       search: search.trim() || undefined,
  //       report_type: "ingredient_stock",       // Fixed for this report only
  //       date_range: date_range || undefined,
  //       start_date: start_date || undefined,
  //       end_date: end_date || undefined,
  //       from_datetime: from_datetime || undefined,
  //       to_datetime: to_datetime || undefined,
  //       export: isExport ? "csv" : undefined,
  //     },
  //     responseType: isExport ? "blob" : "json",
  //   });

  //   if (!isExport) {
  //     return response.data;
  //   }
  //   return response; // blob for export
  // },

  // src/api/reportApi.js

// Update the getIngredientStockReport function to include new filters
getIngredientStockReport: async (params = {}) => {
  const {
    page = 1,
    page_size = 10,
    search = "",
    date_range,
    start_date,
    end_date,
    from_datetime,
    to_datetime,
    category, // Add category parameter
    sub_category, // Add sub_category parameter
    vendor, // Add vendor parameter
    low_stock_only, // Add low_stock_only parameter
    min_stock, // Add min_stock parameter
    max_stock, // Add max_stock parameter
    min_buffer, // Add min_buffer parameter
    max_buffer, // Add max_buffer parameter
    export: exportCsv,
  } = params;

  const isExport = exportCsv === "csv";

  const response = await apiClient.get("/orders/reports/", {
    params: {
      page,
      page_size: isExport ? undefined : page_size,
      search: search.trim() || undefined,
      report_type: "ingredient_stock",
      date_range: date_range || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      from_datetime: from_datetime || undefined,
      to_datetime: to_datetime || undefined,
      category: category || undefined, // Add category to params
      sub_category: sub_category || undefined, // Add sub_category to params
      vendor: vendor || undefined, // Add vendor to params
      low_stock_only: low_stock_only || undefined, // Add low_stock_only to params
      min_stock: min_stock || undefined, // Add min_stock to params
      max_stock: max_stock || undefined, // Add max_stock to params
      min_buffer: min_buffer || undefined, // Add min_buffer to params
      max_buffer: max_buffer || undefined, // Add max_buffer to params
      export: isExport ? "csv" : undefined,
    },
    responseType: isExport ? "blob" : "json",
  });

  if (!isExport) {
    return response.data;
  }
  return response; // blob for export
},
  
  ////////////////////////////////////////////////////////

  // NEW: Canteen Orders using /api/orders/
  getCanteenOrders: async (params = {}) => {
    const {
      page = 1,
      page_size = 10,
      object_id,
      resource,
      action_type,
      search = "",
      date_range,
      start_date,
      end_date,
      from_datetime,
      to_datetime,
    } = params;

    const response = await apiClient.get("/orders/", {
      params: {
        page,
        page_size,
        object_id: object_id || undefined,
        resource: resource || undefined,
        action_type: action_type || undefined,
        search: search.trim() || undefined,
        date_range: date_range || undefined,
        start_date: start_date || undefined,
        end_date: end_date || undefined,
        from_datetime: from_datetime || undefined,
        to_datetime: to_datetime || undefined,
      },
    });

    return response.data;
  },

  // ALL STATS

  getCanteenOrdersStats: async (params = {}) => {
    const {
      page = 1,
      page_size = 10,
      object_id,
      resource,
      action_type,
      search = "",
      date_range,
      start_date,
      end_date,
      from_datetime,
      to_datetime,
    } = params;
    const response = await apiClient.get("/orders/stats/", {
      params: {
        page,
        page_size,
        object_id: object_id || undefined,
        resource: resource || undefined,
        action_type: action_type || undefined,
        search: search.trim() || undefined,
        date_range: date_range || undefined,
        start_date: start_date || undefined,
        end_date: end_date || undefined,
        from_datetime: from_datetime || undefined,
        to_datetime: to_datetime || undefined,
      },
    });

    return response.data;
  },

  getPopularItems: async (params = {}) => {
    const response = await apiClient.get("/reports/popular-items/", { params });
    return response.data; // Expect { popular: [], leastPopular: [], summary: {} }
  },

  getCanteenFinance: async (params = {}) => {
    const response = await apiClient.get("/reports/canteen-finance/", {
      params,
    });
    return response.data; // Expect { collections: [], expenses: [], summary: {} }
  },

  // NEW: Ingredient Stock Report using /api/ingredients/stock-report/
  getIngredientStock: async (params = {}) => {
    const {
      page = 1,
      page_size = 10,
      object_id,
      resource,
      action_type,
      search = "",
      date_range,
      start_date,
      end_date,
      from_datetime,
      to_datetime,
      category,
      sub_category,
      vendor,
      low_stock_only,
    } = params;

    const response = await apiClient.get("/ingredients/stock-report/", {
      params: {
        page,
        page_size,
        object_id: object_id || undefined,
        resource: resource || undefined,
        action_type: action_type || undefined,
        search: search.trim() || undefined,
        date_range: date_range || undefined,
        start_date: start_date || undefined,
        end_date: end_date || undefined,
        from_datetime: from_datetime || undefined,
        to_datetime: to_datetime || undefined,
        category: category || undefined,
        sub_category: sub_category || undefined,
        vendor: vendor || undefined,
        low_stock_only: low_stock_only || undefined,
      },
    });

    return response.data;
  },
};

export default reportApi;
